import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import {
  LOCAL_STORAGE_KEY_PREFIX,
  CLUSTERING_MAP_ID,
  MAP_CLUSTERING_TARGETS_KEY,
  LABEL_DISP_ITEM_KEY,
  LABEL_DISP_ITEM_TARGET,
} from '@/constants.js'
import { getActiveLocale } from '@/i18n'
import { store as coreModule } from 'ifield-core-components'
import mapLayer from './modules/mapLayer.js'
import indoorSite from './modules/indoorSite.js'
import indoorBuilding from './modules/indoorBuilding.js'
import indoorLevel from './modules/indoorLevel.js'
import mapIndoor from './modules/mapIndoor.js'
import checkinMarker from './modules/checkinMarker'
import reportMarker from './modules/reportMarker'
import userMarker from './modules/userMarker'
import userProfile from './modules/userProfile'
import member from './modules/member.js'
import place from './modules/place.js'
import userKeywordTarget from './modules/userKeywordTarget.js'
import userAttribute from './modules/userAttribute.js'
import textTemplate from './modules/textTemplate.js'
import sensor from './modules/sensor.js'
import sensorItem from './modules/sensorItem.js'
import sensorGroup from './modules/sensorGroup.js'
import systemSetting from './modules/systemSetting.js'
import user from './modules/user.js'
import role from './modules/role'
import analyzeTrack from './modules/analyzeTrack'

// ログインコードのlocalStorage保存キー
const LOCAL_STORAGE_KEY_LOGIN_CODE = `${LOCAL_STORAGE_KEY_PREFIX}_loginCode`

// プレイスラベルの更新間隔の個人設定のキー
// 2026-08-20 の打ち合わせで追加が決まった項目。サーバー側は 4.26.0 で
// `place_label_update_interval`（30〜300秒・既定60秒）として実装された
const PLACE_UPDATE_INTERVAL_KEY = 'place_label_update_interval'

Vue.use(Vuex)

const storeModule = {
  state: {
    //ログイン状態保存
    login: {
      userId: null,
      companyCode: null,
      loginCode: null,
      userName: null,
      appType: null,
      isManager: null, // 管理者フラグ
      functionTypes: null, // 利用可能機能タイプ
      mapTypes: null, // 利用可能地図タイプ
      useSample: process.env.NODE_ENV !== 'production', // サンプルページ使用フラグ
    },
    // 受信メッセージ
    receiveMessage: {
      updateInterval: 60, // 更新間隔(秒)
    },
    //タイムライン状態保存
    timeline: {
      // updateTime: '',  // ※ 現在未使用
      updateInterval: 60, // 更新間隔(秒)
    },
    //カレンダーの開始曜日(1:日曜日 2:月曜日)
    calendar: {
      startDayOfWeek: 2,
    },
    // ユーザー履歴
    userHistory: {
      listLimit: {
        value: 1000,
        selectValues: [1000, 3000, 5000],
      },
      historyLineFlg: {
        value: 1,
        selectValues: [1, 2],
      },
    },
    // ユーザー一覧
    userList: {
      listLimit: {
        value: 10,
        selectValues: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
      },
      updateInterval: 60, // 更新間隔(秒)
      positioningThreshold: 10, // 測位中判定時間(分)
      columnStates: [], // リストのカラム状態
      condition: {}, // リストの検索条件
    },
    // 報告書
    report: {
      listLimit: {
        value: 10,
        selectValues: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
      },
      defaultProjectId: null, // 新規登録時のデフォルトプロジェクトID 初期値(前回値なし)はnullとする
    },
    // チェックイン
    checkin: {
      listLimit: {
        value: 10,
        selectValues: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
      },
    },
    // ステイタス
    status: {
      listLimit: {
        value: 10,
        selectValues: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
      },
    },
    // ラベル（地図の吹き出し）の2段目に表示項目の値を出すか
    // 2026-08-20 の打ち合わせの決定3-1。ユーザー／プレイス別・既定OFF
    labelSecondLine: {
      user: false,
      place: false,
    },
    // プレイスラベル（地図のプレイスの吹き出し）
    placeLabel: {
      // 更新間隔(秒)。個人設定（30〜300秒）で変えられる
      // 個人設定が無い環境では 2026-08-20 の打ち合わせで決めた既定の60秒で動く
      updateInterval: 60,
    },
    // ホームポジション
    homePosition: {
      centerLat: 35.692182,
      centerLng: 139.759894,
      zoomLevel: 13,
      heading: 0,
    },
    // 初期値: 全て有効 (['user', 'report', 'status', 'checkin'])
    clusteringTargets: Object.values(CLUSTERING_MAP_ID),
    // アイコンのみ表示設定 (true: アイコン, false: リスト)
    showIconsOnly: true,
    // アプリケーション全体
    app: {
      isReady: false, // ログインに関する一連の処理が終わっているか？
      message: {
        checkBaseDate: null, // 新着メッセージ定期チェック基準日時(日時以降のメッセージがチェック対象)
        enabledCheck: false, // 新着メッセージ定期チェックが有効な場合はtrue
      },
      schedule: {
        lastSaved: {}, // 最後に保存したスケジュールの内容
      },
      user: {
        // 詳細を表示するユーザーID
        profileUserId: null,
      },
    },
    // アプリ全体に影響を及ぼすエラー(認証失敗など)
    error: {
      code: null,
    },
  },
  persistentStates: [
    'login',
    'receiveMessage',
    'timeline',
    'calendar',
    'userHistory',
    'userList',
    'report',
    'checkin',
    'status',
    'labelSecondLine',
    'placeLabel',
    'homePosition',
    'clusteringTargets',
    'showIconsOnly',
    'app.isReady',
    'app.message',
    'app.schedule',
  ],
  mutations: {
    updateLoginState(state, userinfo) {
      if ('userid' in userinfo) {
        state.login.userId = userinfo.userid
      }
      if ('companycode' in userinfo) {
        state.login.companyCode = userinfo.companycode
      }
      if ('logincode' in userinfo) {
        state.login.loginCode = userinfo.logincode
      }
      if ('username' in userinfo) {
        state.login.userName = userinfo.username
      }
      if ('apptype' in userinfo) {
        state.login.appType = userinfo.apptype
      }
      if ('ismanager' in userinfo) {
        state.login.isManager = userinfo.ismanager
      }
      if ('functiontypes' in userinfo) {
        state.login.functionTypes = userinfo.functiontypes
      }
      if ('maptypes' in userinfo) {
        state.login.mapTypes = userinfo.maptypes
      }
    },
    /**
     * ログイン状態をリセットする
     * @param {*} state
     */
    resetLoginState(state) {
      state.login.userId = null
      state.login.loginCode = null
      state.login.userName = null
      state.login.appType = null
      state.login.isManager = null
      state.login.functionTypes = null
      state.login.mapTypes = null
    },
    /**
     * ログインコードをローカルストレージに保存する
     * @param {*} state
     */
    saveLoginCodeToLocal(state) {
      localStorage.setItem(
        LOCAL_STORAGE_KEY_LOGIN_CODE,
        state.login.loginCode ?? ''
      )
    },
    /**
     * ログインコードをローカルストレージから読み出す
     * @param {*} state
     */
    loadLoginCodeFromLocal(state) {
      const loginCode = localStorage.getItem(LOCAL_STORAGE_KEY_LOGIN_CODE)
      if (loginCode) {
        state.login.loginCode = loginCode
      }
    },
    /**
     * ログインコードをローカルストレージから削除する
     */
    removeLoginCodeFromLocal() {
      localStorage.removeItem(LOCAL_STORAGE_KEY_LOGIN_CODE)
    },
    /**
     * メッセージの状態を更新する
     * @param {*} state
     * @param {*} messageInfo 更新内容
     */
    updateReceiveMessageState(state, messageInfo) {
      if (!messageInfo) {
        return
      }

      if ('updateinterval' in messageInfo) {
        state.receiveMessage.updateInterval = messageInfo.updateinterval
      }
    },
    /**
     * タイムラインの状態を更新する
     * @param {*} state
     * @param {*} timelineInfo タイムライン情報
     */
    updateTimelineState(state, timelineInfo) {
      if (!timelineInfo) {
        return
      }

      if ('updateinterval' in timelineInfo) {
        state.timeline.updateInterval = timelineInfo.updateinterval
      }

      // updateTimeは使われていないようなのでコメントアウト
      // var today = new Date()
      // var timestr = today.getHours() + "時" + today.getMinutes() + "分"
      // state.timeline.updateTime = timestr
    },
    /**
     * アプリの情報をリセットする
     * @param {Object} state
     */
    resetAppState(state) {
      state.app.isReady = false
      state.app.message.enabledCheck = false
      state.app.message.checkBaseDate = null
      state.app.schedule.lastSaved = {}
      state.app.user.profileUserId = null
    },
    /**
     * アプリの準備状態を更新する
     * @param {boolean} value
     */
    updateAppIsReady(state, value) {
      state.app.isReady = !!value
    },
    /**
     * アプリのメッセージの情報を更新する
     * @param {Object} state
     * @param {*} message 更新後のメッセージの情報
     */
    updateAppMessageState(state, message) {
      if ('checkBaseDate' in message) {
        state.app.message.checkBaseDate = message.checkBaseDate
      }
      if ('enabledCheck' in message) {
        state.app.message.enabledCheck = message.enabledCheck
      }
    },
    /**
     * カレンダーの開始曜日のを更新する
     * @param {*} state
     * @param {*} calendarInfo カレンダー情報
     */
    updateCalendarState(state, calendarInfo) {
      if (!calendarInfo) {
        return
      }

      if ('startDayOfWeek' in calendarInfo) {
        state.calendar.startDayOfWeek = calendarInfo.startDayOfWeek
      }
    },
    /**
     * ユーザー履歴一覧情報を更新する
     * @param {*} state
     * @param {*} userHistoryInfo ユーザー履歴情報
     */
    updateUserHistoryState(state, userHistoryInfo) {
      if (!userHistoryInfo) {
        return
      }

      if ('listLimit' in userHistoryInfo) {
        state.userHistory.listLimit = userHistoryInfo.listLimit
      }
      if ('historyLineFlg' in userHistoryInfo) {
        state.userHistory.historyLineFlg = userHistoryInfo.historyLineFlg
      }
    },
    /**
     * ユーザー一覧情報を更新する
     * @param {*} state
     * @param {*} userListInfo ユーザー一覧情報
     */
    updateUserListState(state, userListInfo) {
      if (!userListInfo) {
        return
      }

      if ('listLimit' in userListInfo) {
        state.userList.listLimit = userListInfo.listLimit
      }
      if ('updateinterval' in userListInfo) {
        state.userList.updateInterval = userListInfo.updateinterval
      }
      if ('positioningThreshold' in userListInfo) {
        state.userList.positioningThreshold = userListInfo.positioningThreshold
      }
      if ('columnStates' in userListInfo) {
        state.userList.columnStates = userListInfo.columnStates
      }
      if ('condition' in userListInfo) {
        state.userList.condition = userListInfo.condition
      }
    },
    /**
     * 報告書情報を更新する
     * @param {*} state
     * @param {*} reportInfo 報告書情報
     */
    updateReportState(state, reportInfo) {
      if (!reportInfo) {
        return
      }

      if ('listLimit' in reportInfo) {
        state.report.listLimit = reportInfo.listLimit
      }
    },
    /**
     * チェックイン情報を更新する
     * @param {*} state
     * @param {*} checkinInfo チェックイン情報
     */
    updateCheckinState(state, checkinInfo) {
      if (!checkinInfo) {
        return
      }

      if ('listLimit' in checkinInfo) {
        state.checkin.listLimit = checkinInfo.listLimit
      }
    },
    /**
     * ラベルの2段目の表示可否を更新する
     * @param {*} state
     * @param {*} labelSecondLineInfo ラベル2段目の表示可否
     */
    updateLabelSecondLineState(state, labelSecondLineInfo) {
      if (!labelSecondLineInfo) {
        return
      }

      if ('user' in labelSecondLineInfo) {
        state.labelSecondLine.user = labelSecondLineInfo.user
      }
      if ('place' in labelSecondLineInfo) {
        state.labelSecondLine.place = labelSecondLineInfo.place
      }
    },
    /**
     * プレイスラベル情報を更新する
     * @param {*} state
     * @param {*} placeLabelInfo プレイスラベル情報
     */
    updatePlaceLabelState(state, placeLabelInfo) {
      if (!placeLabelInfo) {
        return
      }

      if ('updateinterval' in placeLabelInfo) {
        state.placeLabel.updateInterval = placeLabelInfo.updateinterval
      }
    },
    /**
     * ステイタス情報を更新する
     * @param {*} state
     * @param {*} checkinInfo ステイタス情報
     */
    updateStatusState(state, statusInfo) {
      if (!statusInfo) {
        return
      }

      if ('listLimit' in statusInfo) {
        state.status.listLimit = statusInfo.listLimit
      }
    },
    /**
     * ホームポジション情報を更新する
     * @param {*} state
     * @param {*} homePositionInfo ホームポジション情報
     */
    updateHomePosition(state, homePositionInfo) {
      if (!homePositionInfo) {
        return
      }

      if ('centerLat' in homePositionInfo) {
        state.homePosition.centerLat = homePositionInfo.centerLat
      }
      if ('centerLng' in homePositionInfo) {
        state.homePosition.centerLng = homePositionInfo.centerLng
      }
      if ('zoom' in homePositionInfo) {
        state.homePosition.zoomLevel = homePositionInfo.zoom
      }
      if ('heading' in homePositionInfo) {
        state.homePosition.heading = homePositionInfo.heading
      }
    },
    /**
     * クラスタリング対象マップを更新する
     * @param {Object} state
     * @param {Array<String>} targets マップIDの配列
     */
    updateClusteringTargets(state, targets) {
      state.clusteringTargets = targets
    },
    /**
     * ポップアップの表示スタイルフラグ
     * @param {Object} state
     * @param {Boolean} showIconsOnly アイコンのみ表示する場合は true
     */
    updateShowIconsOnlySetting(state, showIconsOnly) {
      state.showIconsOnly = showIconsOnly
    },

    /**
     * 最後に保存したスケジュール内容を更新する
     * @param {*} state
     * @param {*} schedule
     */
    updateScheduleLastSaved(state, schedule) {
      state.app.schedule.lastSaved = schedule
    },
    /**
     * プロフィール表示対象IDを設定する
     * @param {*} state
     * @param {Number} id ユーザーID
     */
    updateProfileUserId(state, id) {
      state.app.user.profileUserId = id
    },
    setErrorCode(state, code) {
      state.error.code = code
    },
  },
  getters: {
    /**
     * ログインユーザー情報
     */
    loginUser: (state) => {
      return {
        companyCode: state.login.companyCode,
        loginCode: state.login.loginCode,
      }
    },
    // ...
    loginState: (state) => {
      return { 'userId': state.login.userId, 'userName': state.login.userName }
    },
    /**
     * ログインAPIが成功済みの場合はtrue
     */
    isLoggedIn: (state) => !!state.login.userId,
    /**
     * 利用可能機能タイプ(利用不可の場合はnullが返却される)
     * @param typeCode 取得対象の機能タイプコード
     */
    functionType: (state) => (typeCode) => {
      if (!state.login.functionTypes) {
        return null
      }
      return state.login.functionTypes.find(
        (type) => type.functionTypeCode === typeCode
      )
    },
    /**
     * マップのパラメータの一括取得
     *
     *  map-control で、デフォルト値として設定
     *     v-bind="$store.getters.mapControlBinds"
     *
     *  必要に応じて、v-bind:storeSync="true" などで上書きする
     */
    mapControlBinds: (state, getters, rootState, rootGetters) => {
      return {
        basemapLanguage: getActiveLocale(),
        storeSync: true,
        control: true,
        homeCenter: {
          lat: state.homePosition.centerLat,
          lng: state.homePosition.centerLng,
        },
        homeZoom: state.homePosition.zoomLevel,
        homeHeading: state.homePosition.heading,
        heading: rootGetters['map/heading'],
        mapImage: false,
        mapTypeId: rootGetters['map/mapTypeId'],
        layerRenderers: rootGetters['map/layerRenderers'],
        layerMenuData: rootGetters['map/layerMenu/layerMenuData'],
        indoorLevels: rootGetters['mapIndoor/indoorLevels'],
        activeIndoorLevelId: rootGetters['mapIndoor/activeIndoorLevelId'],
        indoorBuilding: rootGetters['mapIndoor/activeIndoorBuilding'],
        mapTypes: state.login.mapTypes,
      }
    },
    /**
     * リスト表示設定
     */
    listViewSetting: (state) => ({
      columnStates: [...state.userList.columnStates],
      condition: { ...state.userList.condition },
    }),
    /**
     * [新規] 指定したマップIDでクラスタリングが有効か判定する
     * 使用例: $store.getters.isClusteringEnabled(CLUSTERING_MAP_ID.USER)
     */
    isClusteringEnabled: (state) => (mapId) => {
      return state.clusteringTargets.includes(mapId)
    },
  },
  actions: {
    /**
     * ログアウト
     * stateの値を初期化する
     */
    logout({ commit, dispatch }) {
      commit('resetLoginState')
      commit('resetAppState')
      dispatch('resetState')
      dispatch('mapLayer/resetState')
      dispatch('mapIndoor/resetState')
      dispatch('indoorSite/resetState')
      dispatch('indoorBuilding/resetState')
      dispatch('indoorLevel/resetState')
      dispatch('checkinMarker/resetState')
      dispatch('reportMarker/resetState')
      dispatch('userMarker/resetState')
      dispatch('userProfile/resetState')
      dispatch('member/resetState')
      dispatch('place/resetState')
      dispatch('userKeywordTarget/resetState')
      dispatch('userAttribute/resetState')
      dispatch('textTemplate/resetState')
      dispatch('sensor/resetState')
      dispatch('sensorItem/resetState')
      dispatch('sensorGroup/resetState')
      dispatch('systemSetting/resetState')
      dispatch('user/resetState')
      dispatch('role/resetState')
    },
    /**
     * アプリのメッセージチェックを有効化する
     */
    enableAppMessageCheck({ commit }) {
      commit('updateAppMessageState', {
        checkBaseDate: new Date(),
        enabledCheck: true,
      })
    },
    /**
     * アプリのメッセージチェックを無効化する
     */
    disableAppMessageCheck({ commit }) {
      commit('updateAppMessageState', {
        enabledCheck: false,
      })
    },
    /**
     * ログインデータ JSON を変換して登録する
     */
    saveLogin({ commit }, loginData) {
      if (loginData.login_code && loginData.user_name && loginData.app_type) {
        let userInfo = {
          'userid': loginData.user_id,
          'companycode': loginData.company_code,
          'logincode': loginData.login_code,
          'username': loginData.user_name,
          'apptype': loginData.app_type,
          'ismanager': loginData.manage_flg,
          'functiontypes': [],
          'maptypes': [],
        }
        loginData.function_types.forEach((type) => {
          userInfo['functiontypes'].push({
            id: type.id,
            functionTypeCode: type.function_type_code,
            functionTypeValue: type.function_type_value,
            functionTypeName: type.function_type_name,
          })
        })
        userInfo['maptypes'] = loginData.map_types
        commit('updateLoginState', userInfo)
        commit('saveLoginCodeToLocal')
      }
    },
    /**
     * ログインユーザーの情報を更新する
     * @param {Object} user 更新後のユーザー情報
     */
    updateLoginUserName({ commit }, userName) {
      commit('updateLoginState', { username: userName })
    },
    /**
     * ユーザー設定をstoreに保存する
     * @param {object}
     * @param {object} settings ユーザー設定(JSON)
     */
    saveUserSetting({ commit }, settings) {
      /**
       * 指定されたitem_keyをもつ項目情報を返却する
       */
      const findSettings = (key) => {
        return settings.find((setting) => setting.item_key === key)
      }

      /**
       * 指定したキーの項目値を数値型で取得する
       */
      const getNumberValue = (key) => {
        const item = findSettings(key)
        if (!item) {
          return undefined
        }
        return Number(item.item_value)
      }

      /**
       * 指定したキーの選択リスト値と選択値を取得する
       */
      const getNumberValueWithSelectValues = (key) => {
        const item = findSettings(key)
        if (!item) {
          return undefined
        }
        const selectValues = item.select_values.map((selectValue) =>
          Number(selectValue.item_value)
        )
        return { value: Number(item.item_value), selectValues: selectValues }
      }

      // タイムライン設定を保存する
      let timelineSettings = {}
      // 更新間隔
      const timelineUpdateInterval = getNumberValue('timeline_update_interval')
      if (timelineUpdateInterval) {
        timelineSettings['updateinterval'] = timelineUpdateInterval
      }
      commit('updateTimelineState', timelineSettings)

      // 受信メール設定を保存する
      let receiveMessageSettings = {}
      // 更新間隔
      const receiveMessageInterval = getNumberValue(
        'receive_message_update_interval'
      )
      if (receiveMessageInterval) {
        receiveMessageSettings['updateinterval'] = receiveMessageInterval
      }
      commit('updateReceiveMessageState', receiveMessageSettings)

      // 地図設定を保存する
      let mapSettings = {}
      // ズームレベル
      const zoomLevel = getNumberValue('map_home_zoom_level')
      if (zoomLevel) {
        mapSettings['zoom'] = Number(zoomLevel)
      }
      // デフォルトの中心緯度経度
      const mapPosition = findSettings('map_home_position')
      if (mapPosition) {
        const coords = mapPosition.item_value.split('|')
        if (coords.length === 2) {
          mapSettings['centerLat'] = Number(coords[0])
          mapSettings['centerLng'] = Number(coords[1])
        }
      }
      // マップ回転角度
      const mapHeading = getNumberValue('map_home_position_angle')
      if (mapHeading != null) {
        mapSettings['heading'] = Number(mapHeading)
      }

      commit('map/updateMapState', mapSettings)
      commit('updateHomePosition', mapSettings)

      // クラスタリング対象マップ (Multi-Select)
      const clusteringTargetsItem = findSettings(MAP_CLUSTERING_TARGETS_KEY)
      if (clusteringTargetsItem && clusteringTargetsItem.item_value) {
        const targets = (
          clusteringTargetsItem.item_value
            ? clusteringTargetsItem.item_value.split('|').filter(Boolean)
            : []
        ).map((code) => {
          const value = clusteringTargetsItem.select_values.find(
            (selectValue) => selectValue.item_code === code
          )
          return value?.item_value
        })
        commit('updateClusteringTargets', targets)
      } else if (clusteringTargetsItem) {
        // 項目は存在するが値が空（全解除）の場合
        commit('updateClusteringTargets', [])
      }
      // クラスタポップアップ表示形式 (Toggle)
      const showIconsOnlyItem = findSettings('map_clustering_show_icons_only')
      if (showIconsOnlyItem) {
        // Toggle型の場合、item_valueは選択された方の item_code になる
        // コード '1' をアイコン表示(true)、'0' をリスト表示(false) と定義する
        const isIconOnly = showIconsOnlyItem.item_value === '1'
        commit('updateShowIconsOnlySetting', isIconOnly)
      }

      // カレンダー開始曜日
      let calendarSetting = {}
      const startDayOfWeek = getNumberValue('calendar_start_week')
      if (startDayOfWeek) {
        calendarSetting['startDayOfWeek'] = startDayOfWeek
      }
      commit('updateCalendarState', calendarSetting)

      // ユーザー一覧
      let userListSetting = {}
      const userListLimit = getNumberValueWithSelectValues('user_list_limit')
      if (userListLimit) {
        userListSetting['listLimit'] = userListLimit
      }
      // 更新間隔
      const userListUpdateInterval = getNumberValue('member_update_interval')
      if (userListUpdateInterval) {
        userListSetting['updateinterval'] = userListUpdateInterval
      }
      // 測位中判定時間
      const userListPositioningThreshold = getNumberValue(
        'positioning_threshold'
      )
      if (userListPositioningThreshold) {
        userListSetting['positioningThreshold'] = userListPositioningThreshold
      }
      commit('updateUserListState', userListSetting)

      // ユーザー履歴一覧
      let userHistorySetting = {}
      const userHistoryListLimit = getNumberValueWithSelectValues(
        'user_history_list_limit'
      )
      if (userListLimit) {
        userHistorySetting['listLimit'] = userHistoryListLimit
      }
      const userHistoryLineFlg =
        getNumberValueWithSelectValues('history_line_flg')
      if (userHistoryLineFlg) {
        userHistorySetting['historyLineFlg'] = userHistoryLineFlg
      }
      commit('updateUserHistoryState', userHistorySetting)

      // 報告書
      let reportSetting = {}
      const reportListLimit =
        getNumberValueWithSelectValues('report_list_limit')
      if (userListLimit) {
        reportSetting['listLimit'] = reportListLimit
      }
      commit('updateReportState', reportSetting)

      // チェックイン
      let checkinSetting = {}
      const checkinListLimit =
        getNumberValueWithSelectValues('checkin_list_limit')
      if (userListLimit) {
        checkinSetting['listLimit'] = checkinListLimit
      }
      commit('updateCheckinState', checkinSetting)

      // ステイタス
      let statusSetting = {}
      const statusListLimit =
        getNumberValueWithSelectValues('status_list_limit')
      if (userListLimit) {
        statusSetting['listLimit'] = statusListLimit
      }
      commit('updateStatusState', statusSetting)

      // プレイスラベル（地図のプレイスの吹き出し）の更新間隔
      // 2026-08-20 の打ち合わせの決定1-3（30〜300秒・既定60秒）。
      // 項目がまだ無い環境では既定の60秒のままになる
      let placeLabelSetting = {}
      const placeUpdateInterval = getNumberValue(PLACE_UPDATE_INTERVAL_KEY)
      if (placeUpdateInterval) {
        placeLabelSetting['updateinterval'] = placeUpdateInterval
      }
      commit('updatePlaceLabelState', placeLabelSetting)

      // ラベル（地図の吹き出し）の2段目を出すか（スロット挿入・トグル2つ）
      // 2026-08-20 の打ち合わせの決定3-1をもとに、2026-08-24 にサーバー側と形を合意した項目。
      // 1項目でユーザー／プレイスの両方を持ち、item_value には選択された方の item_code が
      // '|' で連結されて入る（例 '1|2'）。どちらもOFFなら空文字になる。
      // 項目が無い環境では既定のOFFのままになる
      const labelDispItem = findSettings(LABEL_DISP_ITEM_KEY)
      if (labelDispItem) {
        const selectedCodes = (labelDispItem.item_value || '')
          .split('|')
          .filter(Boolean)
        // item_code（保存値）から item_value（状態のキー）へ読み替える
        const selectedTargets = (labelDispItem.select_values || [])
          .filter((selectValue) =>
            selectedCodes.includes(selectValue.item_code)
          )
          .map((selectValue) => selectValue.item_value)
        commit('updateLabelSecondLineState', {
          user: selectedTargets.includes(LABEL_DISP_ITEM_TARGET.USER),
          place: selectedTargets.includes(LABEL_DISP_ITEM_TARGET.PLACE),
        })
      }
    },
    /**
     * プロフィール表示対象IDの設定
     * @param {Number} id ユーザーID
     */
    updateProfileUserId({ commit }, id) {
      commit('updateProfileUserId', id)
    },
  },
  modules: {
    core: coreModule,
    mapLayer,
    mapIndoor,
    indoorSite,
    indoorBuilding,
    indoorLevel,
    checkinMarker,
    reportMarker,
    userMarker,
    userProfile,
    member,
    place,
    userKeywordTarget,
    userAttribute,
    textTemplate,
    sensor,
    sensorItem,
    sensorGroup,
    systemSetting,
    user,
    role,
    analyzeTrack,
  },
}

/**
 * モジュールの永続化キーを収集する
 * @param {Object} modules モジュール
 * @returns {Array.<String>} 永続化するstateのキー(ピリオドで結合)
 */
const gatherSubModulePersistentStates = (modules) => {
  return Object.entries(modules)
    .map(([key, module]) => {
      const states = []
      if (module.persistentStates) {
        states.push(
          ...module.persistentStates.map((state) => `${key}.${state}`)
        )
      }
      if (module.modules) {
        const subModuleStates = gatherSubModulePersistentStates(module.modules)
        states.push(...subModuleStates.map((state) => `${key}.${state}`))
      }
      return states
    })
    .flat()
}

/**
 * sessionStorageに保存するキー
 */
const persistentPaths = [
  ...storeModule.persistentStates,
  ...gatherSubModulePersistentStates(storeModule.modules),
]

// リロード時にもstore情報をsessionStorageに保存することで再ログインを防ぐ
storeModule.plugins = [
  createPersistedState({
    key: 'iField4_usersWeb_store',
    storage: window.sessionStorage,
    paths: persistentPaths,
  }),
]

const store = new Vuex.Store(storeModule)

export { store as default, storeModule }
